import React, { useCallback } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { ProductMoreColorsContent } from "@app/scripts/react-product-more-colors/components/ProductMoreColors";
import { ProductPrice } from "@app/scripts/react-product/components/ProductPrice";
import { useLocaleContext } from "@app/scripts/react-providers/locales";
import { ProductMediaItems } from "@app/scripts/react-product-details/components/ProductMediaItems";
import { ProductSizeSelect } from "@app/scripts/react-product-details/components/ProductSizeSelect";

import type { NormalisedProduct } from "@app/scripts/types";

type Props = {
  product: NormalisedProduct;
  toggleOpen?: () => void;
  productMediaSliderDesktop?: boolean;
  columnsDesktop?: number;
  columnsMobile?: number;
  smallButtons?: boolean;
  scrollbarMobile?: boolean;
};

export const ProductDetails: React.FC<Props> = ({
  product,
  toggleOpen,
  productMediaSliderDesktop = false,
  columnsDesktop = 2,
  columnsMobile = 1,
  smallButtons = false,
  scrollbarMobile = false,
}) => {
  const { error, handleAddToCart, handleChangeVariant, selectedVariantId, title } = useShopifyProduct({ product });
  const { product: productLocale } = useLocaleContext();

  const addToCart = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      const success = await handleAddToCart(e);
      if (success) {
        toggleOpen?.();
      }
    },
    [handleAddToCart]
  );

  return (
    /* data-* attributes not used in solution. Requested by analyzify */
    <div className="product quick-add" data-variant={selectedVariantId} data-handle={product?.handle} data-title={product?.title}>
      <item-carousel
        columns-desktop={columnsDesktop}
        columns-mobile={columnsMobile}
        disabled-mobile="false"
        disabled-desktop={`${!productMediaSliderDesktop}`}
        small-btns={smallButtons}
        scrollbar-mobile={scrollbarMobile}
      >
        <div className="product-grid item-carousel columns-desktop__2 columns-mobile__1">
          <ProductMediaItems product={product} />
        </div>
      </item-carousel>

      <div className="page-width">
        <div className="product__title">
          <h2 className="h1">{title}</h2>
        </div>

        <ProductPrice product={product} />

        <ProductMoreColorsContent product={product} />

        <form method="post" action="/cart/add" onSubmit={addToCart} className="product__form">
          <input name="id" value={selectedVariantId} type="hidden" />

          <h2 className="text-lg bold">{productLocale?.select_size || "Select Size"}</h2>
          <ProductSizeSelect
            variants={product.variants}
            handleChangeVariant={handleChangeVariant}
            defaultTitle={productLocale?.select_size}
          />

          {error && <small className="form__message text-brand-error">{error}</small>}

          <div className="product__add-to-cart flex">
            <Button
              type="submit"
              label={productLocale?.add_to_cart || "Add to bag"}
              className="add-to-cart"
              variant="secondary"
            />
            <Button
              href={`/products/${product.handle}`}
              variant="text"
              label={productLocale?.view_details || "View Details"}
              className="product__auto-h details"
            />
          </div>
        </form>
      </div>
    </div>
  );
};
